import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import { MapContainer, TileLayer, GeoJSON, Marker, LayersControl, CircleMarker, Tooltip, Polygon, useMap } from "react-leaflet";
import { ArrowLeft, Crosshair, Maximize2 } from "lucide-react";
import { get } from "@/lib/api";
import { useEventDrawer } from "@/components/EventDrawer";

const PALETTE = ["#1d4ed8", "#b45309", "#047857", "#be123c", "#6d28d9", "#0e7490", "#a16207", "#4d7c0f", "#c2410c", "#0f766e"];
const colorOf = (insee) => PALETTE[[...insee].reduce((a, c) => a + c.charCodeAt(0), 0) % PALETTE.length];
const IGN = (layer, fmt) => `https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=${layer}&STYLE=normal&TILEMATRIXSET=PM&FORMAT=${fmt}&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}`;
const BASES = [
  ["OpenStreetMap France (rues, bâtiments, POI)", "https://{s}.tile.openstreetmap.fr/osmfr/{z}/{x}/{y}.png", 20, "© OpenStreetMap France / contributeurs OSM"],
  ["OpenStreetMap standard", "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", 19, "© contributeurs OpenStreetMap"],
  ["Plan IGN", IGN("GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2", "image/png"), 19, "© IGN – Géoplateforme"],
  ["Photographies aériennes IGN", IGN("ORTHOIMAGERY.ORTHOPHOTOS", "image/jpeg"), 19, "© IGN – Géoplateforme"],
];

const label = (name, color) => L.divIcon({
  className: "", iconSize: null,
  html: `<div style="transform:translate(-50%,-50%);white-space:nowrap;font:600 11px 'IBM Plex Sans',sans-serif;color:${color};background:rgba(255,255,255,.85);border:1px solid ${color};border-radius:3px;padding:1px 5px;box-shadow:0 1px 2px rgba(0,0,0,.15)">${name}</div>`,
});

const WORLD = [[-89, -179.9], [-89, 179.9], [89, 179.9], [89, -179.9]];
const holesOf = (g) => {
  const polys = g.type === "Polygon" ? [g.coordinates] : g.type === "MultiPolygon" ? g.coordinates : [];
  return polys.map((poly) => poly[0].map(([lon, lat]) => [lat, lon]));
};

function Fitter({ fc, focus, view }) {
  const map = useMap();
  useEffect(() => {
    if (focus || !fc?.features?.length) return;
    const b = L.geoJSON(fc).getBounds();
    if (b.isValid()) map.fitBounds(b, { padding: [30, 30] });
  }, [fc, focus, map]);
  useEffect(() => {
    if (!focus) return;
    if (view === "whole" && focus.geometry.type !== "Point") map.fitBounds(L.geoJSON(focus).getBounds(), { padding: [30, 30] });
    else map.setView([focus.properties.lat, focus.properties.lon], 16);
  }, [focus, view, map]);
  return null;
}

const FocusPanel = ({ f, view, setView, onBack }) => {
  const p = f.properties;
  const btn = "inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-[3px] border transition-colors duration-150";
  return (
    <div data-testid="commune-focus-panel" className="absolute top-3 left-12 right-3 sm:right-auto z-[500] bg-white/95 dark:bg-slate-900/95 border border-slate-200 dark:border-slate-700 rounded-[4px] shadow-sm p-3 sm:w-80">
      <button data-testid="focus-back-btn" onClick={onBack} className="text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-slate-100 inline-flex items-center gap-1 mb-1"><ArrowLeft className="h-3 w-3" />Vue d'ensemble du territoire</button>
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded-sm" style={{ background: colorOf(p.insee) }} />
        <h2 data-testid="focus-commune-name" className="font-display text-lg font-bold">{p.name}</h2>
      </div>
      <div className="text-[11px] font-mono text-slate-500">{(p.postal_codes || []).join(", ")} · INSEE {p.insee}{p.population ? ` · ${p.population.toLocaleString("fr-FR")} hab.` : ""}</div>
      <div className="text-xs mt-1">{p.upcoming_events} événement(s) à venir{!p.active && " · surveillance désactivée"}</div>
      <div className="flex gap-1.5 mt-2">
        <button data-testid="focus-center-btn" onClick={() => setView("center")} className={`${btn} ${view === "center" ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900" : "border-slate-300 dark:border-slate-600"}`}><Crosshair className="h-3 w-3" />Centre (rues, POI)</button>
        <button data-testid="focus-whole-btn" onClick={() => setView("whole")} className={`${btn} ${view === "whole" ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900" : "border-slate-300 dark:border-slate-600"}`}><Maximize2 className="h-3 w-3" />Commune entière</button>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Zoomez pour afficher noms de rues, bâtiments et points d'intérêt. Fond « Plan IGN » ou « Photographies aériennes » via le sélecteur de couches.</p>
    </div>
  );
};

export default function CommunesMap() {
  const { open } = useEventDrawer();
  const [focus, setFocus] = useState(null);
  const [showEvents, setShowEvents] = useState(false);
  const [view, setView] = useState("center");
  const [listOpen, setListOpen] = useState(false);
  const select = (insee) => { setFocus(insee); setView("center"); setListOpen(false); };
  const { data: fc } = useQuery({ queryKey: ["communes-geojson"], queryFn: () => get("/communes/geojson") });
  const { data: ev } = useQuery({ queryKey: ["map-events-overlay"], enabled: showEvents, queryFn: () => get("/events", { has_coords: true, page_size: 1000 }) });
  const feats = useMemo(() => (fc?.features || []).filter((f) => f.geometry), [fc]);
  const focused = feats.find((f) => f.properties.insee === focus);
  const shown = focused ? [focused] : feats;
  useEffect(() => {
    const k = (e) => e.key === "Escape" && setFocus(null);
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  return (
    <div className="flex h-[calc(100dvh-var(--chrome-h))] relative" data-testid="communes-map-page">
      <aside className={`${listOpen ? "flex" : "hidden"} md:flex flex-col absolute md:static inset-0 z-[600] md:z-auto bg-white dark:bg-slate-900 w-full md:w-72 shrink-0 border-r border-slate-200 dark:border-slate-800 overflow-y-auto`}>
        <button data-testid="communes-list-close" className="md:hidden self-end m-2 text-xs underline" onClick={() => setListOpen(false)}>Fermer la liste</button>
        <div className="p-3 border-b border-slate-200 dark:border-slate-800">
          <h1 className="font-display text-lg font-bold">Carte du territoire</h1>
          <p className="text-[11px] text-slate-500">{feats.length} commune(s) surveillée(s) · contours officiels (geo.api.gouv.fr) · fonds OpenStreetMap / IGN</p>
          <label className="mt-2 flex items-center gap-2 text-xs">
            <input data-testid="toggle-events-overlay" type="checkbox" checked={showEvents} onChange={(e) => setShowEvents(e.target.checked)} />
            Afficher les événements géolocalisés
          </label>
        </div>
        <ul data-testid="communes-map-list">
          {feats.map((f) => {
            const p = f.properties;
            return (
              <li key={p.insee}>
                <button data-testid={`map-commune-${p.insee}`} onClick={() => select(p.insee)}
                  className={`w-full text-left px-3 py-2 border-b border-slate-100 dark:border-slate-800 text-xs transition-colors duration-150 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${focus === p.insee ? "bg-slate-100 dark:bg-slate-800" : ""}`}>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: colorOf(p.insee), opacity: p.active ? 1 : 0.35 }} />
                    <span className="font-semibold">{p.name}</span>
                    {!p.active && <span className="text-[9px] font-mono text-slate-400">DÉSACTIVÉE</span>}
                    <span className="ml-auto font-mono text-[10px] text-slate-500">{p.upcoming_events} év.</span>
                  </div>
                  <div className="pl-4.5 ml-[18px] text-[10px] text-slate-500 font-mono">{(p.postal_codes || []).join(", ")} · INSEE {p.insee}{p.population ? ` · ${p.population.toLocaleString("fr-FR")} hab.` : ""}</div>
                </button>
              </li>
            );
          })}
          {fc && !feats.length && <li className="p-3 text-xs text-slate-400">Aucune commune surveillée. Ajoutez-en dans l'onglet Communes.</li>}
        </ul>
      </aside>
      <div className="flex-1 relative isolate" data-testid="communes-map-container">
        <MapContainer center={[46.6, 2.5]} zoom={6} maxZoom={20} className="h-full w-full">
          <LayersControl position="topright">
            {BASES.map(([name, url, maxZoom, attribution], i) => (
              <LayersControl.BaseLayer key={name} name={name} checked={i === 0}>
                <TileLayer url={url} maxZoom={maxZoom} maxNativeZoom={Math.min(maxZoom, 19)} attribution={attribution} />
              </LayersControl.BaseLayer>
            ))}
          </LayersControl>
          {focused && focused.geometry.type !== "Point" && (
            <Polygon positions={[WORLD, ...holesOf(focused.geometry)]} pathOptions={{ stroke: false, fillColor: "#0f172a", fillOpacity: 0.28 }} interactive={false} />
          )}
          {shown.map((f) => {
            const p = f.properties, color = colorOf(p.insee);
            return f.geometry.type === "Point" ? (
              <CircleMarker key={p.insee} center={[p.lat, p.lon]} radius={8} pathOptions={{ color }} />
            ) : (
              <GeoJSON key={p.insee} data={f}
                style={{ color, weight: focus === p.insee ? 3 : 2, fillOpacity: focus === p.insee ? 0 : 0.05, dashArray: p.active ? null : "6 4" }}
                eventHandlers={{ click: () => select(p.insee) }} />
            );
          })}
          {!focused && feats.filter((f) => f.properties.lat).map((f) => (
            <Marker key={`l-${f.properties.insee}`} position={[f.properties.lat, f.properties.lon]} icon={label(f.properties.name, colorOf(f.properties.insee))}
              eventHandlers={{ click: () => select(f.properties.insee) }} />
          ))}
          {showEvents && (ev?.items || []).map((e) => (
            <CircleMarker key={e.id} center={[e.lat, e.lon]} radius={5} pathOptions={{ color: "#0f172a", fillColor: "#f59e0b", fillOpacity: 0.9, weight: 1 }}
              eventHandlers={{ click: () => open(e.id) }}>
              <Tooltip>{e.title}</Tooltip>
            </CircleMarker>
          ))}
          <Fitter fc={fc} focus={focused} view={view} />
        </MapContainer>
        {!listOpen && <button data-testid="communes-list-open" onClick={() => setListOpen(true)} className="md:hidden absolute bottom-4 fab-bottom left-3 z-[500] h-8 px-3 text-xs rounded-[3px] border border-slate-300 bg-white dark:bg-slate-900 shadow">Liste des communes</button>}
        {focused && <FocusPanel f={focused} view={view} setView={setView} onBack={() => setFocus(null)} />}
      </div>
    </div>
  );
}
